// What the app tab HAS, flattened to id + label pairs for the console's target
// pickers. Without this the console can only ever hit "the first match"; with it
// a command can be aimed at a named client, booking, artist or deposit.

import { useBrainStore } from '@/state/brainStore';
import { useSimulationStore } from '@/state/simulationStore';
import { customerName, simTimeFull } from '@/utils/format';
import type { ControlRoster, RosterEntry } from '@/events/controlChannel';
import type { Booking, CustomerRecord, Deposit } from '@/domain/types';

/** Kept small deliberately: the roster rides on every status broadcast. */
const CAP = 60;

function bookingLabel(booking: Booking, brain: ReturnType<typeof useBrainStore.getState>): string {
  const customer = brain.customers[booking.customerId];
  const artist = brain.artists[booking.artistId];
  const who = customer ? customerName(customer) : booking.customerId;
  return `${simTimeFull(booking.start)} · ${who} · ${artist?.displayName ?? booking.artistId} (${booking.status})`;
}

function depositLabel(deposit: Deposit, brain: ReturnType<typeof useBrainStore.getState>): string {
  const customer = brain.customers[deposit.customerId];
  const who = customer ? customerName(customer) : deposit.customerId;
  return `$${deposit.amountAud} · ${who} · ${deposit.status}`;
}

/**
 * Rank customers by how likely the facilitator is to want them: anyone in the
 * queue sequence first, then anyone with a deposit or a booking, then the rest.
 * Without this the picker is 2,000 procedural names deep and useless.
 */
function customerRank(customer: CustomerRecord, brain: ReturnType<typeof useBrainStore.getState>): number {
  if (brain.sequenceState[customer.id]) return 0;
  if (Object.values(brain.deposits).some((d) => d.customerId === customer.id)) return 1;
  if (Object.values(brain.bookings).some((b) => b.customerId === customer.id)) return 2;
  return 3;
}

export function buildControlRoster(): ControlRoster {
  const brain = useBrainStore.getState();
  const today = useSimulationStore.getState().clockIso.slice(0, 10);

  const studios: RosterEntry[] = Object.values(brain.studios).map((s) => ({
    id: s.id,
    label: `${s.name} (${s.brand})`,
  }));

  const artists: RosterEntry[] = Object.values(brain.artists).map((a) => ({
    id: a.id,
    label: `${a.displayName} · ${a.studioId}`,
  }));

  const customers: RosterEntry[] = Object.values(brain.customers)
    .map((c) => ({ c, rank: customerRank(c, brain) }))
    .filter((x) => x.rank < 3)
    .sort((a, b) => a.rank - b.rank)
    .slice(0, CAP)
    .map((x) => ({ id: x.c.id, label: customerName(x.c) }));

  // Today first (that is the demo day), then whatever else is live, newest last
  // so the list reads in time order.
  const bookings: RosterEntry[] = Object.values(brain.bookings)
    .filter((b) => b.status !== 'superseded')
    .sort((a, b) => {
      const aToday = a.start.slice(0, 10) === today ? 0 : 1;
      const bToday = b.start.slice(0, 10) === today ? 0 : 1;
      return aToday !== bToday ? aToday - bToday : a.start.localeCompare(b.start);
    })
    .slice(0, CAP)
    .map((b) => ({ id: b.id, label: bookingLabel(b, brain) }));

  const deposits: RosterEntry[] = Object.values(brain.deposits)
    .slice(0, CAP)
    .map((d) => ({ id: d.id, label: depositLabel(d, brain) }));

  return { studios, artists, customers, bookings, deposits };
}
